/** Entry-flow API shapes: partition insights + the pull-target decision.
 *  Served by GET /pull-plan/entry-insights (services/pull_plan/entry.py). */

export interface EntryTargetLeafDate {
  wbs_id: number | null;
  leaf_name: string;
  activity_id: number;
  task_code: string;
  date: string | null; // ISO YYYY-MM-DD
}

/** An outside successor of the pending-delete set, grouped by base activity
 *  name (the part before "|"), ranked as a pull-target candidate. */
export interface EntryTargetCandidate {
  label: string;
  /** Takt_Work of the instances, when tagged — display signal only. */
  work_code: string;
  milestone_ish: boolean;
  fed_by_ties: number;
  leaf_count: number;
  total_leafs: number;
  score: number;
  per_leaf: EntryTargetLeafDate[];
}

export interface EntryInsights {
  counts: {
    total: number;
    kept: number;
    to_replace: number;
    external_ties: number;
  };
  candidates: EntryTargetCandidate[];
}

/** The entry decision, stored verbatim in the draft payload under
 *  `pull_target`. Any of the three modes counts as "chosen" and unlocks
 *  Start Plan. */
export type PullTargetChoice =
  | {
      mode: "milestone";
      label: string;
      work_code: string;
      per_leaf: EntryTargetLeafDate[];
    }
  | {
      mode: "custom";
      per_leaf: Array<{ wbs_id: number; leaf_name: string; date: string }>;
    }
  | { mode: "skip" };

/** Validates a parsed JSON value matches the PullTargetChoice union before it's
 *  trusted as the draft's pull-target. Guards the load path against a backend
 *  payload whose shape has drifted. */
export function isPullTargetChoice(obj: unknown): obj is PullTargetChoice {
  if (!obj || typeof obj !== "object") return false;
  const mode = (obj as { mode?: unknown }).mode;
  if (mode === "skip") return true;
  if (mode === "milestone") {
    const o = obj as { label?: unknown; per_leaf?: unknown };
    return typeof o.label === "string" && Array.isArray(o.per_leaf);
  }
  if (mode === "custom") {
    return Array.isArray((obj as { per_leaf?: unknown }).per_leaf);
  }
  return false;
}
